// French (vous) copy for /on-demand-ftp, translated from copy.en.ts. Same shape (checked by copy.test.ts).
// "FTP à la demande"; "l'outil d'envoi web" for the web uploader. French typography: a non-breaking space before
// : ; ? ! and in 10 000. «Label» (no inner spaces) is the page's markup for a button or setting as the app shows
// it: the uploader and FileZilla are in English, so translations keep these labels; other quotes use “ ”.
import type { OnDemandFtpCopy } from "./copy.en";

const fr: OnDemandFtpCopy = {
  seo: {
    title: "Comment fonctionne le FTP à la demande | Photohawk",
    description:
      "Si le FTP vous est indispensable, chaque membre de votre espace de travail peut démarrer son propre serveur FTP Photohawk depuis l'outil d'envoi web, sur toutes les offres. Prêt en 30 secondes environ, à une adresse personnelle qui ne change jamais, il s'arrête tout seul après une heure sans activité.",
  },
  hero: {
    badge: "Toutes les offres",
    eyebrow: "Disponible dès maintenant",
    title: "Comment fonctionne le FTP à la demande",
    subtitle:
      "Si le FTP vous est indispensable, démarrez votre propre serveur FTP depuis l'outil d'envoi web, quand vous en avez besoin. Il est prêt en 30 secondes environ et s'arrête tout seul quand vous avez fini.",
  },
  before: {
    title: "Avant de commencer\u00a0: l'outil d'envoi web est plus rapide",
    body: "L'outil d'envoi web est la façon standard d'envoyer vos photos, sur toutes les offres.",
    points: [
      { id: "parallel", text: "Il envoie de nombreuses photos à la fois, bien plus qu'un logiciel FTP comme FileZilla (2 à la fois par défaut, et 10 au maximum)." },
      { id: "adaptive", text: "Il s'adapte à votre connexion pour en tirer pleinement parti." },
      { id: "live", text: "Les aperçus partent en premier\u00a0: votre galerie est en ligne en quelques minutes pendant que les originaux suivent." },
      { id: "resume", text: "Si votre connexion coupe, il reprend tout seul." },
    ],
    button: "Ouvrir l'outil d'envoi web",
    link: "Découvrir le nouvel outil d'envoi web",
  },
  what: {
    heading: "Votre propre serveur FTP, quand vous en avez besoin",
    body: "Chaque membre de votre espace de travail peut démarrer son propre serveur FTP depuis l'outil d'envoi web, sur toutes les offres. Chaque personne a son propre serveur, à une adresse personnelle qui ne change jamais\u00a0: vous ne configurez votre logiciel FTP qu'une seule fois. Rien ne tourne quand vous ne l'utilisez pas\u00a0: il s'arrête tout seul après une heure sans activité.",
    date: "1er novembre 2026",
    closing: "Nos serveurs FTP partagés, actifs en permanence, ferment le 1er novembre 2026. Vous les utilisez aujourd'hui\u00a0? Démarrez votre propre serveur et changez l'hôte dans votre logiciel FTP.",
  },
  steps: {
    heading: "Comment ça marche",
    subtitle: "Quatre étapes la première fois. Ensuite, il suffit de démarrer votre serveur et d'envoyer vos photos.",
    items: [
      { id: "open", title: "Ouvrez l'outil d'envoi web", body: "Allez sur `upload.photohawk.com` et connectez-vous, ou ouvrez l'onglet «Upload» d'une galerie." },
      { id: "start", title: "Démarrez votre serveur", body: "Cliquez sur «FTP server» en haut, puis sur «Start FTP server». Il est prêt en 30 secondes environ." },
      { id: "connect", title: "Copiez l'adresse et les identifiants dans votre logiciel FTP", body: "Votre adresse, le port 21, votre e-mail et votre mot de passe Photohawk. L'adresse ne change jamais." },
      { id: "upload", title: "Envoyez vos photos", body: "Ajoutez vos photos dans le dossier de la galerie. Le serveur s'arrête après une heure sans envoi." },
    ],
    button: "Démarrer votre serveur FTP",
    buttonNote: "Ouvre l'outil d'envoi web avec votre serveur FTP prêt à démarrer.",
  },
  panel: {
    heading: "Ce que vous verrez",
    body: "Tout se trouve dans le panneau «Your FTP server» de l'outil d'envoi web, avec un bouton de copie à côté de chaque information.",
    points: [
      { id: "address", text: "Votre adresse, par exemple `swift-heron-4821.ftp.photohawk.com`. Elle est à vous pour de bon\u00a0: vous ne la configurez qu'une fois." },
      { id: "ip", text: "L'adresse IP du serveur est aussi affichée. Elle change à chaque démarrage du serveur\u00a0: utilisez plutôt l'adresse quand c'est possible." },
      { id: "countdown", text: "Un compte à rebours jusqu'à l'arrêt, avec «Keep it running» et «Stop»." },
    ],
    alt: "Le panneau “Your FTP server” affichant Ready en Europe (Irlande), avec l'hôte swift-heron-4821.ftp.photohawk.com, l'adresse IP, le port 21, le nom d'utilisateur alex@example.com, le mode passif et le FTP sur TLS, ainsi qu'un compte à rebours avec les boutons Keep it running et Stop.",
  },
  details: {
    heading: "Vos informations de connexion",
    subtitle: "Saisissez-les dans votre logiciel FTP.",
    rows: [
      { id: "host", label: "Hôte", value: "Votre adresse, par exemple `swift-heron-4821.ftp.photohawk.com` (ou l'adresse IP affichée)" },
      { id: "port", label: "Port", value: "21" },
      { id: "user", label: "Nom d'utilisateur", value: "Votre e-mail Photohawk" },
      { id: "password", label: "Mot de passe", value: "Votre mot de passe Photohawk, ou votre mot de passe FTP (voir plus bas)" },
      { id: "mode", label: "Mode de transfert", value: "Passif" },
      { id: "encryption", label: "Chiffrement", value: "FTP simple, ou FTPS (FTP explicite sur TLS). Pour le FTPS, utilisez le nom d'hôte, pas l'adresse IP." },
    ],
    switching: "Vous utilisiez déjà le FTP de Photohawk\u00a0? Gardez votre nom d'utilisateur, votre mot de passe et vos dossiers. Changez seulement l'hôte pour votre nouvelle adresse.",
  },
  password: {
    heading: "Un mot de passe réservé au FTP, si vous le souhaitez",
    body: "Plutôt que d'enregistrer votre mot de passe Photohawk dans un logiciel FTP, cliquez sur «Create FTP password» dans le panneau. Il ne fonctionne que sur votre serveur FTP, avec votre e-mail comme nom d'utilisateur. Vous pouvez le copier, en créer un nouveau (l'ancien cesse de fonctionner en moins d'une minute) ou le supprimer à tout moment. Votre mot de passe Photohawk continue aussi de fonctionner.",
  },
  folders: {
    heading: "Vos dossiers",
    body: "Vous retrouvez les mêmes dossiers qu'avant\u00a0:",
    path: "/Galleries/<gallery>/<collection>",
    points: [
      { id: "single", text: "Une galerie avec une seule collection est simplement `/Galleries/<gallery>`." },
      { id: "workspaces", text: "Vous envoyez vers plusieurs espaces de travail\u00a0? Chacun a son propre dossier, avec ses galeries à l'intérieur." },
      { id: "create", text: "Créez d'abord la galerie et ses collections dans Photohawk. Vos photos apparaissent dans la galerie en quelques instants, comme avec l'outil d'envoi web." },
    ],
  },
  off: {
    heading: "Il s'arrête tout seul",
    body: "Votre serveur s'arrête après 1 heure sans activité\u00a0: vous n'avez rien à retenir. Le panneau affiche un compte à rebours.",
    points: [
      { id: "keep", text: "«Keep it running» relance le compte à rebours sans rien envoyer." },
      { id: "stop", text: "«Stop» l'arrête immédiatement quand vous avez terminé." },
      { id: "caps", text: "Par sécurité, il s'arrête aussi après 12 heures sans envoi en cours, et au plus tard après 24 heures." },
      { id: "again", text: "Redémarrez-le quand vous voulez, avec la même adresse et les mêmes identifiants." },
    ],
  },
  regions: {
    heading: "Régions",
    body: "Faites tourner votre serveur en Europe (Irlande), dans l'est des États-Unis (Virginie du Nord) ou en Asie-Pacifique (Malaisie). La région la plus proche est choisie pour vous, et vous pouvez la changer avant le démarrage.",
  },
  filezilla: {
    heading: "Exemple\u00a0: configurer FileZilla",
    subtitle: "Les autres logiciels FTP utilisent les mêmes réglages, parfois sous des noms un peu différents.",
    steps: [
      { id: "site", text: "Ouvrez «File → Site Manager» et cliquez sur «New site»." },
      { id: "protocol", text: "Protocole\u00a0: «FTP – File Transfer Protocol»." },
      { id: "host", text: "Hôte\u00a0: votre adresse, par exemple `swift-heron-4821.ftp.photohawk.com`. Port\u00a0: `21`." },
      { id: "tls", text: "Chiffrement\u00a0: «Use explicit FTP over TLS if available»." },
      { id: "login", text: "Type d'authentification\u00a0: «Normal». Utilisateur\u00a0: votre e-mail Photohawk. Mot de passe\u00a0: votre mot de passe FTP ou votre mot de passe Photohawk." },
      { id: "passive", text: "Dans l'onglet «Transfer Settings», choisissez «Passive». Cliquez sur «Connect», puis ouvrez `Galleries`." },
    ],
    tip: "Vous envoyez un gros dossier\u00a0? FileZilla envoie 2 fichiers à la fois par défaut, et 10 au maximum. Vous pouvez monter son réglage de transferts simultanés jusqu'à 10 («Settings → Transfers»), ou utiliser l'outil d'envoi web, qui en envoie bien plus à la fois.",
  },
  trouble: {
    heading: "Si quelque chose ne fonctionne pas",
    items: [
      { id: "connect", title: "Impossible de se connecter", body: "Vérifiez que le panneau indique «Ready». Si le nom d'hôte vient d'être créé, utilisez l'adresse IP pendant une minute." },
      { id: "listing", title: "Connecté, mais la liste des dossiers ne s'affiche pas", body: "Activez le mode passif." },
      { id: "logins", title: "“Too many failed logins”", body: "Attendez 15 minutes, puis vérifiez le nom d'utilisateur (votre e-mail) et le mot de passe." },
    ],
  },
  faq: {
    heading: "Questions",
    items: [
      {
        q: "Pourquoi mon serveur s'est-il arrêté\u00a0?",
        a: "Il s'arrête après une heure sans activité, pour ne jamais tourner quand vous ne l'utilisez pas. Redémarrez-le à tout moment\u00a0: l'adresse et les identifiants restent les mêmes. Pour le garder actif sans rien envoyer, cliquez sur «Keep it running». Il s'arrête aussi après 12 heures sans envoi en cours, et au plus tard après 24 heures.",
      },
      {
        q: "Deux personnes peuvent-elles avoir un serveur en même temps\u00a0?",
        a: "Oui. Chaque membre de votre espace de travail peut démarrer son propre serveur, un chacun, à sa propre adresse. Personne d'autre que vous ne peut se connecter au vôtre.",
      },
      {
        q: "Est-ce sécurisé\u00a0?",
        a: "Oui. Avec le FTPS (FTP sur TLS) sur votre nom d'hôte, la connexion est chiffrée avec un certificat de confiance. Seuls vos identifiants fonctionnent sur votre serveur, et après plusieurs échecs de connexion, le serveur bloque cette connexion pendant 15 minutes.",
      },
      {
        q: "Est-ce payant\u00a0?",
        a: "Non. Votre propre serveur FTP est inclus dans toutes les offres.",
      },
      {
        q: "Est-il inclus dans toutes les offres\u00a0?",
        a: "Oui, Lite compris. Au quotidien, l'outil d'envoi web ou Photohawk Studio sont plus rapides\u00a0; votre propre serveur FTP est là quand le FTP est indispensable.",
      },
      {
        q: "Que devient l'ancien FTP partagé\u00a0?",
        a: "Les serveurs FTP partagés, actifs en permanence, ferment le 1er novembre 2026 et fonctionnent comme avant jusque-là. Pour continuer à utiliser le FTP, démarrez votre propre serveur et remplacez l'hôte dans votre logiciel FTP par votre nouvelle adresse. Votre nom d'utilisateur, votre mot de passe et vos dossiers ne changent pas.",
      },
    ],
  },
  cta: {
    heading: "Prêt quand vous l'êtes",
    body: "Pour vos envois de tous les jours, utilisez l'outil d'envoi web. Quand le FTP est indispensable, votre propre serveur est à un clic.",
    primary: "Ouvrir l'outil d'envoi web",
    secondary: "Démarrer votre serveur FTP",
  },
};

export default fr;
